import * as moment from 'moment-timezone';
import { AuthenticationService } from '~src/authentication/services/AuthenticationService';
import { Formatters, Messages } from '~src/codegen/Localize';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { LoginErrorCodes, UserErrorCode } from '~src/ts/helpers/ErrorCodes';
import React = require('react');
import { useEffect } from 'react';

// We will ignore lockout messages where the remaining time is less than this value.
const MINIMUM_TIME_DIFF_FOR_BACKOFF = 1000; // 1 second

function getWaitInMillisUntil(nextAttempt: Date): number {
  const now = moment();
  const timeDiff = moment.duration(moment(nextAttempt).diff(now)).asMilliseconds();
  return timeDiff;
}

function shouldWaitForRateLimiting(nextAttempt: Date): boolean {
  return getWaitInMillisUntil(nextAttempt) > MINIMUM_TIME_DIFF_FOR_BACKOFF;
}

export interface LocalUsernameAndPasswordRateLimitingProps {
  // We provide a handler to the parent component that we will use to process the authentication failure.
  // It returns a promise that will be resolved when returning control to the parent component.
  populateHandler: (handler: (error: IError, username: string) => Promise<void>) => void;
}

export function LocalUsernameAndPasswordRateLimiting(props: LocalUsernameAndPasswordRateLimitingProps) {

  const [lockedOutUntil, setLockedOutUntil] = React.useState(null);
  const [lockedOutMessage, setLockedOutMessage] = React.useState(null);

  useEffect(() => {
    props.populateHandler(handleLocalAuthErrors);
  }, [props.populateHandler]);

  useEffect(() => {
    if (!(lockedOutUntil && shouldWaitForRateLimiting(lockedOutUntil))) {
    // No need to wait any longer, cleanup.
    setLockedOutMessage(null);
      setLockedOutUntil(null);
      return;
    }

    // Re-evaluate and update the message after one second.
    const intervalId = setInterval(() => {
      setLockedOutMessage(
        Messages.passwordLockoutPeriod({ RELATIVE_TIME: Formatters.formatLongBestFitRelativeTime(lockedOutUntil)})
      );
    }, 1000);

    return () => clearInterval(intervalId);
  }, [lockedOutUntil, lockedOutMessage]);

  function handleLocalAuthErrors(error: IError, username: string): Promise<void> {
    if (FeatureFlags.localAuthLoginRateLimitingEnabled &&
      (error.code === UserErrorCode.LoginAttemptRejected || error.code === LoginErrorCodes.BadCredentials)) {
      // Do rate limiting and give back control by resolving the promise once it's over.
      return handleFailedAttemptsRateLimiting(username);
    }

    // Nothing to do here, just return control.
    return Promise.resolve();
  }

  function handleFailedAttemptsRateLimiting(username: string): Promise<void> {
    setLockedOutUntil(null);

    return AuthenticationService.getNextLocalAuthLoginAttempt(username).then((nextAttempt: Date) => {
      if (shouldWaitForRateLimiting(nextAttempt)) {
        setLockedOutUntil(nextAttempt);
        return new Promise<void>((resolve, reject) => {
          // Call me back after waiting. 
          setTimeout(() => {
            resolve();
          }, getWaitInMillisUntil(nextAttempt));
        });
      } else {
        // No point on blocking the UI / showing a message the user won't notice the wait.
        return Promise.resolve();
      }
    }).catch(() => {
      // If there's any error fetching the data, just unblock, we did our best to offer good UX.
      return Promise.resolve();
    });
  }

  return lockedOutMessage &&
      <div className={'tb-login-msg-container'}>
        <div className={'tb-login-msg tb-login-error'} tb-test-id="message-failure" role="alert">
          {lockedOutMessage}
        </div>
      </div>;
}
